import React from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  KeyRound,
  Handshake,
  Compass,
  Users2,
  ShieldCheck,
  ScrollText,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionHeader, Card, PrincipleChip, ComplianceBand } from "@/components/Section";

const HERO_IMG =
  "https://images.unsplash.com/photo-1755780164376-c492e18eba47?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxUYWxsaW5uJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3ODM0MDgwN3ww&ixlib=rb-4.1.0&q=85";

const PRESENCE_IMG =
  "https://images.unsplash.com/photo-1764296377290-51123bc0cbce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxjbGVhbiUyMGNvbW1lcmNpYWwlMjBidWlsZGluZ3xlbnwwfHx8fDE3NzgzNDA4MDd8MA&ixlib=rb-4.1.0&q=85";

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="container-tight pt-16 md:pt-24 pb-16 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="overline reveal" data-testid="hero-overline">
                Estonia · Real Estate Activities
              </div>
              <h1
                data-testid="hero-title"
                className="reveal reveal-delay-1 mt-6 font-display text-4xl sm:text-5xl lg:text-[64px] font-bold tracking-tight text-navy leading-[1.04]"
              >
                Taonga&nbsp;OÜ
              </h1>
              <p
                data-testid="hero-subtitle"
                className="reveal reveal-delay-2 mt-6 text-xl sm:text-2xl text-charcoal font-light max-w-2xl leading-snug"
              >
                Estonian company active in real estate activities, rental operations and property-related projects.
              </p>
              <p
                data-testid="hero-text"
                className="reveal reveal-delay-3 mt-6 text-[16px] leading-relaxed text-charcoal/85 max-w-2xl"
              >
                Taonga OÜ focuses on property-related activities including
                acquisition, rental and operational management support through
                professional partnerships and local real estate networks in
                Estonia. The company may also maintain selected long-term
                corporate shareholdings complementary to its core activities.
              </p>
              <div className="reveal reveal-delay-4 mt-10 flex flex-wrap items-center gap-4">
                <Button
                  asChild
                  data-testid="hero-learn-more-btn"
                  className="bg-navy hover:bg-navy/90 text-white rounded-md h-11 px-6 text-[15px]"
                >
                  <Link to="/about">
                    Learn More <ArrowRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  data-testid="hero-contact-btn"
                  className="border-charcoal/20 text-charcoal hover:text-navy hover:border-warm rounded-md h-11 px-6 text-[15px]"
                >
                  <Link to="/contact">Contact</Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-5 reveal reveal-delay-2">
              <div className="relative">
                <div className="absolute -inset-2 bg-warm/10 -z-10 translate-x-3 translate-y-3 rounded-md" />
                <img
                  src={HERO_IMG}
                  alt="Modern architecture in Tallinn, Estonia"
                  className="w-full h-[420px] lg:h-[520px] object-cover rounded-md border border-border"
                  loading="eager"
                  data-testid="hero-image"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm border border-border rounded-md px-4 py-3 flex items-center gap-3">
                  <Compass className="h-4 w-4 text-warm" strokeWidth={1.5} />
                  <span className="text-[12px] uppercase tracking-[0.18em] text-charcoal">
                    Tallinn · Estonia
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CORE ACTIVITIES */}
      <section className="section bg-white border-t border-border" data-testid="core-activities-section">
        <div className="container-tight">
          <SectionHeader
            overline="Core Activities"
            title="A focused real estate operating model"
            lead="Property-related activities supported by professional partnerships and a long-term operational approach."
            testId="core-activities-header"
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card
              icon={Building2}
              title="Property Activities"
              testId="core-card-property-activities"
            >
              Acquisition, holding and management of residential and commercial properties either directly or through subsidiaries and local partners.
            </Card>
            <Card
              icon={KeyRound}
              title="Rental Operations"
              testId="core-card-rental-operations"
            >
              Support and management of rental activities, tenant relationships and property-related operations with a long-term approach.
            </Card>
            <Card
              icon={Handshake}
              title="Professional Partnerships"
              testId="core-card-partnerships"
            >
              Cooperation with local real estate professionals, property service providers and selected real estate and crowdfunding platforms.
            </Card>
          </div>
        </div>
      </section>

      {/* LOCAL PRESENCE */}
      <section className="section bg-soft" data-testid="local-presence-section">
        <div className="container-tight">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <img
                src={PRESENCE_IMG}
                alt="Modern commercial building facade"
                className="w-full h-[380px] lg:h-[460px] object-cover rounded-md border border-border"
                loading="lazy"
                data-testid="presence-image"
              />
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="overline">Local Presence</div>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-[44px] font-bold tracking-tight text-navy leading-[1.1]">
                Operating through local networks in Tallinn
              </h2>
              <p className="mt-6 text-[17px] leading-relaxed text-charcoal max-w-2xl">
                Taonga OÜ works with local real estate professionals and service
                providers in Tallinn and evaluates selected property-related
                opportunities through professional networks and established
                platforms.
              </p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-4 max-w-xl">
                {[
                  "Local real estate agents",
                  "Property service providers",
                  "Rental counterparties",
                  "Established platforms",
                ].map((label) => (
                  <li
                    key={label}
                    className="flex items-start gap-3 text-[15px] text-charcoal"
                  >
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-warm shrink-0" />
                    {label}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PROPERTY PLATFORMS & NETWORKS */}
      <section className="section bg-white border-t border-border" data-testid="platforms-section">
        <div className="container-tight">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <SectionHeader
                overline="Networks"
                title="Property Platforms & Networks"
                testId="platforms-header"
              />
            </div>
            <div className="lg:col-span-7">
              <p className="text-[17px] leading-relaxed text-charcoal max-w-2xl">
                Taonga OÜ may evaluate selected property-related opportunities
                through established European real estate platforms such as
                Estateguru and Crowdestate, as well as through local
                professional networks and counterparties in Estonia.
              </p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-4 max-w-xl">
                {[
                  "Estateguru",
                  "Crowdestate",
                  "Local professional networks",
                  "Estonian counterparties",
                ].map((label) => (
                  <li
                    key={label}
                    className="flex items-start gap-3 text-[15px] text-charcoal"
                  >
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-warm shrink-0" />
                    {label}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* BUSINESS PRINCIPLES */}
      <section className="section bg-soft" data-testid="principles-section">
        <div className="container-tight">
          <SectionHeader
            overline="Business Principles"
            title="How we operate"
            lead="A conservative, partnership-oriented approach grounded in transparency."
            testId="principles-header"
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Compass, label: "Long-term approach" },
              { icon: Users2, label: "Professional counterparties" },
              { icon: ScrollText, label: "Transparent business relationships" },
              { icon: ShieldCheck, label: "Conservative operational model" },
            ].map(({ icon: Icon, label }, i) => (
              <div
                key={label}
                data-testid={`principle-card-${i}`}
                className="hover-lift bg-white border border-border rounded-md p-7"
              >
                <Icon className="h-5 w-5 text-warm mb-5" strokeWidth={1.5} />
                <div className="font-display text-[16px] font-semibold text-navy leading-snug">
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUSINESS MODEL */}
      <ComplianceBand overline="Business Model" testId="home-compliance-band">
        Taonga OÜ operates exclusively with its own corporate capital and does
        not provide regulated financial services, brokerage, portfolio
        management or investment advice to third parties.
      </ComplianceBand>
    </div>
  );
};

export default Home;
